import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClinicalDocument, Encounter } from '@/features/encounters/api';
import { api } from '@/lib/api';

export const REFERRAL_URGENCIES = [
  'routine',
  'within_2_weeks',
  'urgent_today',
] as const;

export type ReferralUrgency = (typeof REFERRAL_URGENCIES)[number];

export interface ReferralLetterInput {
  specialty: string;
  hospital: string;
  urgency: ReferralUrgency;
  reason: string;
}

function isUrgency(value: unknown): value is ReferralUrgency {
  return REFERRAL_URGENCIES.includes(value as ReferralUrgency);
}

function parseInput(value: unknown): ReferralLetterInput | null {
  if (!value || typeof value !== 'object') return null;
  const draft = value as Partial<ReferralLetterInput>;
  if (
    typeof draft.specialty !== 'string' ||
    typeof draft.hospital !== 'string' ||
    !isUrgency(draft.urgency) ||
    typeof draft.reason !== 'string'
  )
    return null;
  return {
    specialty: draft.specialty,
    hospital: draft.hospital,
    urgency: draft.urgency,
    reason: draft.reason,
  };
}

/** GET/PUT /encounters/:id/referral-draft (API RL-1860). */
export interface ReferralDraft {
  draft: ReferralLetterInput | null;
  updatedAt: string | null;
}

const draftKey = (encounterId: string) => ['referral-draft', encounterId];

function toDraft(value: { draft: unknown; updatedAt: string | null }) {
  return { draft: parseInput(value.draft), updatedAt: value.updatedAt };
}

/**
 * RL-1860: the letter being written is kept on the server (one per
 * encounter), so it opens on any computer. Only the encounter's doctor reads
 * it while the encounter is open; signing removes it.
 */
export function useReferralDraft(encounterId: string, enabled: boolean) {
  return useQuery({
    queryKey: draftKey(encounterId),
    enabled,
    queryFn: async ({ signal }): Promise<ReferralDraft> =>
      toDraft(
        (
          await api.get<{ draft: unknown; updatedAt: string | null }>(
            `/encounters/${encounterId}/referral-draft`,
            { signal },
          )
        ).data,
      ),
  });
}

export function useSaveReferralDraft(encounterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReferralLetterInput): Promise<ReferralDraft> =>
      toDraft(
        (
          await api.put<{ draft: unknown; updatedAt: string | null }>(
            `/encounters/${encounterId}/referral-draft`,
            input,
          )
        ).data,
      ),
    onSuccess: (d) => qc.setQueryData(draftKey(encounterId), d),
  });
}

export function latestReferral(encounter: Encounter): ClinicalDocument | null {
  return (
    encounter.documents
      .filter((document) => document.kind === 'referral_letter')
      .sort(
        (a, b) =>
          new Date(b.issuedAt).getTime() - new Date(a.issuedAt).getTime(),
      )[0] ?? null
  );
}

export function referralInputFromDocument(
  document: ClinicalDocument,
): ReferralLetterInput | null {
  return parseInput(document.data);
}

export function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

export function referralDefaultReason(encounter: Encounter): string {
  return encounter.diagnoses
    .map((diagnosis) => `${diagnosis.code} ${diagnosis.description}`)
    .join(' · ');
}
